import type {ReactElement} from 'react';
import React, {useEffect, useState} from 'react';
import {Thumbnail} from '@Components/thumbnail';
import {ThumbnailType} from '@Components/thumbnail/thumbnail.types';
import LogoFull from '@Assets/logo-postermaker-light.svg';
import {Button, Size, Type} from '@Components/button';
import {onUserInactive} from '@Utils/activity.util';
import TooltipTrigger from '@Components/tooltip-trigger';
import {TOOLTIP_POSITION} from '@Components/tooltip';
import {TooltipTriggerEvent} from '@Components/tooltip-trigger/tooltip-trigger';
import {redirectUser} from '@Utils/browser.util';
import styles from './poster-web-page-nav-bar.module.scss';
import useWindowSize from '@Hooks/useWindowSize';
import QRCode from 'qrcode';

const INACTIVE_USER_DELAY = 6000;

interface PosterWebPageNavBarProps {
  canEditPoster: boolean;

  qrCodeUrl: string;

  isFullScreen: boolean;

  onToggleFullScreen(): void;
}

export function PosterWebPageNavBar({...props}: PosterWebPageNavBarProps): ReactElement<any> {
  const [isNavBarVisible, setIsNavBarVisible] = useState(true);
  const {windowWidth} = useWindowSize();
  const showButtonText = windowWidth > 500;

  useEffect((): void => {
    onUserInactive(hideNavBar, showNavBar, INACTIVE_USER_DELAY);
  }, []);

  const hideNavBar = (): void => {
    setIsNavBarVisible(false);
  };

  const showNavBar = (): void => {
    setIsNavBarVisible(true);
  };

  const onToggleFullScreen = (): void => {
    props.onToggleFullScreen();
  };

  const onCopyShareableLink = (): void => {
    const link = window.posterEditor?.whiteboard?.getWebpageLink();
    if (link) {
      window.PMW.copyTextToClipboard(link);
    }
  };

  const onEdit = (): void => {
    const link = window.posterEditor?.whiteboard?.getEditLink();
    if (link) {
      redirectUser(link, false, true);
    }
  };

  const onDownloadQrCode = async (): Promise<void> => {
    const qrDataUrl = await QRCode.toDataURL(props.qrCodeUrl, {
      width: 130,
      margin: 0,
      errorCorrectionLevel: 'M',
    });
    const link = document.createElement('a');
    link.href = qrDataUrl;
    link.download = 'QR-Code.png';
    link.click();
    link.remove();
  };

  return (
    <div id="nav" className={`${styles.nav} ${isNavBarVisible ? styles.navIn : ''} flex-justify-between flex-items-center`}>
      <Thumbnail
        data={{
          type: ThumbnailType.IMAGE,
          imageSrc: LogoFull as string,
        }}
        onClick={(): void => {
          window.location.href = window.PMW.util.site_url('');
        }}
        className={`${styles.logo} spacing-m-l-2`}
      />
      <div className="flexbox">
        {props.canEditPoster ? (
          <Button
            onClick={onDownloadQrCode}
            customClasses="spacing-m-r-2 -noun"
            size={Size.SMALL}
            type={Type.DARK_GHOST}
            text={showButtonText ? window.i18next.t('pmwjs_download_qr_code') : ''}
            icon="icon-qr-content"
          />
        ) : null}
        {props.canEditPoster ? (
          <Button
            onClick={onEdit}
            customClasses="spacing-m-r-2"
            size={Size.SMALL}
            type={Type.DARK_GHOST}
            text={showButtonText ? window.i18next.t('pmwjs_edit') : ''}
            icon="icon-pencil"
          />
        ) : null}
        <Button
          onClick={onToggleFullScreen}
          customClasses="spacing-m-r-2"
          size={Size.SMALL}
          type={Type.DARK_GHOST}
          text={showButtonText ? (props.isFullScreen ? window.i18next.t('pmwjs_exit_fullscreen') : window.i18next.t('pmwjs_fullscreen')) : ''}
          icon={props.isFullScreen ? 'icon-exit-fullscreen' : 'icon-fit-to-screen'}
        />
        <TooltipTrigger text={window.i18next.t('pmwjs_link_copied')} position={TOOLTIP_POSITION.TOP_MIDDLE} triggerEvent={TooltipTriggerEvent.CLICK}>
          <Button
            onClick={onCopyShareableLink}
            size={Size.SMALL}
            type={Type.DARK_GHOST}
            text={showButtonText ? window.i18next.t('pmwjs_copy_shareable_link') : ''}
            icon="icon-link"
          />
        </TooltipTrigger>
      </div>
    </div>
  );
}
